import { useState, useEffect } from 'react';
import { Table, Loader, Group } from '@mantine/core';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import Header from '../../components/util/header';
import { Language } from '../../languages/languageHandler';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import PaginationComponent from '../../components/pagination';
import { formatCurrAmount, getLocationName } from '../../utils/script';
import { ICurrency } from '../../../libraries/currencies';
import {
  fetchUserShiftReport,
  fetchUserShiftsList,
  fetchUserShiftsListXLS,
  UserShift,
  UserShiftsResponse
} from '../../../libraries/user-shift';
import CustomModal from '../../components/custom-modal';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

const UserShifts = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();
  const [userShiftList, setUserShiftsList] = useState<UserShiftsResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData | undefined>(undefined);
  const [offset, setOffset] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [, /* statusOperCode */ setStatusOperCode] = useState<number>();

  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Fit rows
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });

  const getUserShiftList = () => {
    if (loading) {
      controller.abort();
    }
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setUserShiftsList(undefined);
    const shouldFetchTotalRows = offset === 0;

    fetchUserShiftsList(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      filterBarData.period || 0,
      offset,
      limit,
      shouldFetchTotalRows,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.uid
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setUserShiftsList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRows(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  // Initial load (no filters)
  useEffect(() => {
    getUserShiftList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Update when limit, offset, or filters change
  useEffect(() => {
    getUserShiftList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, offset, filterBarData]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1); // Reset to first page when filters change
  };

  const changePage = (n: number) => {
    const newOffset = (n - 1) * limit;
    setCurrentPage(n);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSListReport = async () => {
    if (!filterBarData) return;

    try {
      await fetchUserShiftsListXLS(
        filterBarData.entity || 0,
        filterBarData.lid || 0,
        filterBarData.eid || 0,
        filterBarData.period || 0,
        controller,
        formatDateToISO(filterBarData.beginDateTime),
        formatDateToISO(filterBarData.endDateTime),
        filterBarData.uid
      );
    } catch (err: any) {
      console.error('XLS error:', err);

      const status = err?.response?.statusText || err.message;
      const message = `${lang.USER_SHIFTS_REPORT_ERROR} - ${status}`;
      setErrorMessage(message);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const getXLSReport = async (row: UserShift) => {
    if (!filterBarData) return;

    try {
      await fetchUserShiftReport(
        row.eid || 0,
        filterBarData.entity || 0,
        row.user?.uid || 0,
        row.shift || 0,
        controller
      );
    } catch (err: any) {
      console.error('XLS error:', err);

      const status = err?.response?.statusText || err.message;
      const message = `${lang.USER_SHIFTS_REPORT_ERROR} - ${status}`;
      setErrorMessage(message);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  return (
    <div className="user-shift-page">
      <Header pageTitle={lang.USER_SHIFT_TITLE} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={getUserShiftList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          beginDateTime: true,
          endDateTime: true,
          period: true
        }}
        xlsReport={!noResults ? getXLSListReport : undefined}
      />

      <Table.ScrollContainer minWidth={500}>
        <Table striped verticalSpacing="sm" ref={tableRef as any}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>EID</Table.Th>
              <Table.Th>{lang.LOGIN_USER}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_OPEN}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}</Table.Th>
              <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
              <Table.Th>{lang.USER_SHIFTS_METHODS_CASH}</Table.Th>
              <Table.Th>{lang.USER_SHIFTS_METHODS_CARD}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : noResults ? (
              <Table.Tr>
                <Table.Td colSpan={8} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              userShiftList?.list.map((row, index) => {
                // Find the matching currency for this row
                const matchingCurrency = currencies?.find(c => c.cid === row?.total?.cid);

                return (
                  <Table.Tr key={index}>
                    <Table.Td>{getLocationName(row.sid)}</Table.Td>
                    <Table.Td>{row.eid}</Table.Td>
                    <Table.Td>{row.user ? `${row.user.uid} - ${row.user.name}` : '-'}</Table.Td>
                    <Table.Td>{formatDateTimeNSecLabel(row.dateTimeOpen)}</Table.Td>
                    <Table.Td>{formatDateTimeNSecLabel(row.dateTimeClose)}</Table.Td>
                    <Table.Td>
                      {matchingCurrency ? formatCurrAmount(row?.total?.total || 0, matchingCurrency) : '-'}
                    </Table.Td>
                    <Table.Td>
                      {matchingCurrency
                        ? formatCurrAmount((row?.total?.paid ?? 0) - (row?.total?.paidAP ?? 0), matchingCurrency)
                        : '-'}
                    </Table.Td>
                    <Table.Td>
                      {matchingCurrency ? formatCurrAmount(row?.total?.paidAP ?? 0, matchingCurrency) : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Icon.IconFileTypeXls
                        stroke={2}
                        onClick={() => getXLSReport(row)}
                        style={{ cursor: 'pointer' }}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          statusOperCode={0}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </div>
  );
};

export default UserShifts;
